import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GitBranch, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppReleaseHistoryPanel } from './AppReleaseHistoryPanel';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  FLOATING_KEYBOARD_ANIM_MS,
  useFloatingKeyboardSlideReveal,
} from '../../utils/learnerSlideModalMotion';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';

const VERSION_JOURNEY_CLOSE_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;

export interface AppReleaseHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

export const AppReleaseHistoryModal: React.FC<AppReleaseHistoryModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const { direction } = useAppLocale();
  const panelRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const finishCloseRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const finishClose = useCallback(() => {
    if (finishCloseRef.current) return;
    finishCloseRef.current = true;
    isClosingRef.current = false;
    onClose();
    setMounted(false);
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return;
    isClosingRef.current = true;
    setVisible(false);
    if (!visible) {
      window.requestAnimationFrame(() => {
        if (!finishCloseRef.current) finishClose();
      });
    }
  }, [visible, finishClose]);

  const onHistoryBack = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return true;
    requestClose();
    return false;
  }, [requestClose]);

  useHistoryBackClose(open, onHistoryBack, 'app-release-history');

  useLayoutEffect(() => {
    if (open) {
      finishCloseRef.current = false;
      isClosingRef.current = false;
      setMounted(true);
      setVisible(prefersReducedMotion());
      return undefined;
    }
    isClosingRef.current = true;
    setVisible(false);
    return undefined;
  }, [open]);

  const revealPanel = useCallback(() => setVisible(true), []);
  useFloatingKeyboardSlideReveal(open && mounted && !visible && !isClosingRef.current, revealPanel);

  const handleExitTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (visible || !isClosingRef.current) return;
      finishClose();
    },
    [visible, finishClose]
  );

  useEffect(() => {
    if (visible || !mounted || !isClosingRef.current) return undefined;
    const timer = window.setTimeout(finishClose, VERSION_JOURNEY_CLOSE_ANIM_MS + 50);
    return () => window.clearTimeout(timer);
  }, [visible, mounted, finishClose]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, requestClose]);

  if (!mounted) return null;

  const modal = (
    <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
          visible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="flex min-h-full items-end justify-center px-3 sm:px-6">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="version-journey-modal-title"
          className={`learner-calm-modal keyboard-slide-modal-panel keyboard-slide-modal-panel--slide-up pointer-events-auto relative flex h-[min(85vh,780px)] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-slate-200/90 bg-gradient-to-b from-white via-slate-50 to-amber-50/40 shadow-2xl dark:border-slate-700/90 dark:from-gray-900 dark:via-gray-950 dark:to-amber-950/20 sm:rounded-2xl${
            visible ? ' is-visible' : ''
          }`}
          onTransitionEnd={handleExitTransitionEnd}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative shrink-0 overflow-hidden border-b border-amber-200/60 bg-gradient-to-r from-amber-100/90 via-white to-violet-100/70 px-5 py-4 dark:border-amber-900/40 dark:from-amber-950/50 dark:via-gray-900 dark:to-violet-950/30 sm:px-6">
            <div
              className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-amber-400/20 blur-2xl dark:bg-amber-500/10"
              aria-hidden
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-600/25">
                  <GitBranch className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 text-start" dir={direction}>
                  <h2
                    id="version-journey-modal-title"
                    className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white sm:text-xl"
                  >
                    {t('versionJourney')}
                  </h2>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 sm:text-sm">
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                    {t('versionJourneySubtitle')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={requestClose}
                className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-white/80 hover:text-slate-800 dark:hover:bg-gray-800 dark:hover:text-slate-100"
                aria-label={t('closeVersionJourney')}
                title={tCommon('close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-5 py-5 sm:px-6 sm:py-6">
            <AppReleaseHistoryPanel active={mounted} />
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
};

export default AppReleaseHistoryModal;
